import { useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';
import { clinicPaymentService } from '@/services/clinicPaymentService';

interface PaymentAccountConfigAlertProps {
  className?: string;
  showButton?: boolean;
  action?: () => void;
  actionText?: string;
  variant?: 'admin' | 'staff'; // 'admin' shows for admins with button, 'staff' shows for non-admins without button
}

const buttonVariants: Variants = {
  rest: { scale: 1 },
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

export const PaymentAccountConfigAlert: React.FC<PaymentAccountConfigAlertProps> = ({
  className = '',
  showButton = true,
  action,
  actionText = 'Complete onboarding',
  variant = 'admin',
}) => {
  const { toast } = useToast();
  const { clinic, isAdmin, isGuided } = useClinic();
  const [linkLoading, setLinkLoading] = useState(false);

  // Support both camelCase and snake_case from API
  const pa = clinic?.paymentAccount ?? (clinic as { payment_account?: typeof clinic.paymentAccount })?.payment_account;
  const chargesEnabled = pa?.stripe_charges_enabled ?? (pa as { stripe_charges_enabled?: boolean })?.stripe_charges_enabled;

  // Payment is "ready" only when we have an account AND Stripe is fully onboarded (charges enabled)
  const hasPaymentAccount = !!pa;
  const stripeFullyOnboarded = hasPaymentAccount && chargesEnabled === true;
  const paymentReady = stripeFullyOnboarded;

  // Show banner to everyone when payment not ready; variant only affects message and button (admin sees button, staff sees contact-admin message)
  const isStaffVariant = variant === 'staff' && !isAdmin;
  const needsOnboarding = hasPaymentAccount && !stripeFullyOnboarded;

  // Hide banner when payment is fully ready
  if (paymentReady) {
    return null;
  }

  // For guided clinics: only hide when there is no payment account (nothing to set up).
  // If guided has a payment account but Stripe is not fully onboarded, still show the banner.
  if (isGuided && !hasPaymentAccount) {
    return null;
  }

  const handleCompleteOnboarding = async () => {
    if (action) {
      action();
      return;
    }
    if (!clinic?.id || linkLoading) return;
    setLinkLoading(true);
    try {
      let result = await clinicPaymentService.refreshStripeOnboardingLink(clinic.id);
      if (!result?.onboardingUrl) {
        result = await clinicPaymentService.startStripeOnboarding(clinic.id);
      }
      if (result?.onboardingUrl) {
        window.location.href = result.onboardingUrl;
      } else {
        toast({
          title: 'Could not get onboarding link',
          description: 'Please try again or complete setup in Settings → Payments.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Could not get onboarding link',
        description: 'Please try again or complete setup in Settings → Payments.',
        variant: 'destructive',
      });
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <motion.div
      className={`relative w-full rounded-xl border border-brand-terracotta/20 bg-white p-4 shadow-sm ${className}`}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <div className='flex items-center space-x-2'>
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <AlertTriangle className='h-4 w-4 text-brand-terracotta' />
        </motion.div>
        <div className='flex-1 text-sm text-foreground'>
          <div className='flex items-center justify-between'>
            <span>
              {isStaffVariant
                ? needsOnboarding
                  ? 'Your clinic’s payment setup is not finished. Ask your clinic administrator to complete the setup in Payments so your clinic can accept card payments.'
                  : 'Your clinic hasn’t set up payments yet. Ask your clinic administrator to connect a payment account so the clinic can accept payments from patients.'
                : needsOnboarding
                  ? 'Complete your clinic’s payment setup so you can accept card payments from patients. Finish the remaining steps in Payments.'
                  : 'Connect a payment account so your clinic can accept and receive payments from patients.'}
            </span>
          </div>
        </div>
        {!isStaffVariant && showButton && (
          <motion.div variants={buttonVariants} initial='rest' whileHover='hover' whileTap='tap'>
            <Button
              size='sm'
              className='ml-4 rounded-full bg-brand-teal text-white hover:bg-brand-teal/90'
              onClick={handleCompleteOnboarding}
              disabled={linkLoading}
            >
              {linkLoading ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Generating link…
                </>
              ) : (
                actionText
              )}
            </Button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default PaymentAccountConfigAlert;
